'use client';

/**
 * BookingCalendar — SPEC-MARKETPLACE-BOOKING-CALENDAR (Phase 5).
 *
 * Airbnb-style calendar component for booking service products (cabins,
 * hotels, classes, appointments). Buyers select a check-in date + check-out
 * date; the calendar shows availability (available, booked, blocked, weekday/
 * weekend unavailable). The total + deposit is calculated and displayed.
 *
 * Spec ref: SPEC-MARKETPLACE-BOOKING-CALENDAR §6.1.
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';

interface AvailabilityDay {
  date: string;
  available: boolean;
  reason?: 'blocked' | 'booked' | 'weekday_unavailable' | 'weekend_unavailable';
  price?: string;
}

interface BookingCalendarProps {
  productId: string;
  pricePerNight: string;
  depositPercent: number;
  cancellationPolicy: 'free_48h' | 'no_refund';
  checkInTime?: string;
  checkOutTime?: string;
  onBookingCreated?: (bookingId: string) => void;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export function BookingCalendar({
  productId,
  pricePerNight,
  depositPercent,
  cancellationPolicy,
  checkInTime,
  checkOutTime,
  onBookingCreated,
}: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<Map<string, AvailabilityDay>>(new Map());
  const [loading, setLoading] = useState(false);
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);

  // Fetch availability for the current month + next month.
  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0);
      const startStr = start.toISOString().slice(0, 10);
      const endStr = end.toISOString().slice(0, 10);

      const data = await api.booking.getAvailability(productId, startStr, endStr) as { data: AvailabilityDay[] };
      const map = new Map<string, AvailabilityDay>();
      for (const day of data.data) {
        map.set(day.date, day);
      }
      setAvailability(map);
    } catch {
      // PR fix (Founder report 2026-11-03): "el sistema de reservas le
      // aparece tachado a los usuarios, cuando deberian estar disponibles".
      // If the availability API fails OR returns empty, ALL dates were
      // showing as `available: false` → `line-through` (unavailable).
      // Fix: when the fetch fails or returns no data, set availability to
      // empty map. The rendering logic below now treats dates with NO
      // availability data as AVAILABLE by default (only explicitly blocked
      // dates show as unavailable). This matches Airbnb behavior: dates
      // are available unless the host explicitly blocks them.
      setAvailability(new Map());
    } finally {
      setLoading(false);
    }
  }, [productId, currentMonth]);

  useEffect(() => {
    void fetchAvailability();
  }, [fetchAvailability]);

  const handleDateClick = (dateStr: string) => {
    // PR fix: if there's no availability data for this date, it's
    // available by default (only dates explicitly marked as
    // available:false are blocked).
    const day = availability.get(dateStr);
    if (day && !day.available) return;

    if (!checkIn || (checkIn && checkOut)) {
      // Start a new selection.
      setCheckIn(dateStr);
      setCheckOut(null);
    } else if (dateStr > checkIn) {
      // Set check-out (must be after check-in).
      // Verify all dates between checkIn and dateStr are available.
      const start = new Date(checkIn + 'T00:00:00.000Z');
      const end = new Date(dateStr + 'T00:00:00.000Z');
      let allAvailable = true;
      const current = new Date(start);
      current.setUTCDate(current.getUTCDate() + 1); // skip check-in day
      while (current < end) {
        const ds = current.toISOString().slice(0, 10);
        const d = availability.get(ds);
        // PR fix: dates with no availability data are available by default.
        // Only explicitly blocked dates (available:false) break the range.
        if (d && !d.available) {
          allAvailable = false;
          break;
        }
        current.setUTCDate(current.getUTCDate() + 1);
      }
      if (allAvailable) {
        setCheckOut(dateStr);
      } else {
        toast.error('Hay fechas no disponibles en el rango seleccionado');
      }
    } else {
      // Clicked before check-in → reset to new check-in.
      setCheckIn(dateStr);
      setCheckOut(null);
    }
  };

  const handleReserve = async () => {
    if (!checkIn || !checkOut) return;
    setBooking(true);
    try {
      const data = await api.booking.create(productId, checkIn, checkOut) as { data: { bookingId: string } };
      toast.success('Reserva creada', {
        description: `Tu reserva del ${checkIn} al ${checkOut} está confirmada.`,
      });
      onBookingCreated?.(data.data.bookingId);
    } catch (err) {
      toast.error('No se pudo crear la reserva', {
        description: err instanceof Error ? err.message : 'Intenta de nuevo.',
      });
    } finally {
      setBooking(false);
    }
  };

  // Calculate nights + total + deposit.
  const nights = checkIn && checkOut
    ? Math.round((new Date(checkOut + 'T00:00:00.000Z').getTime() - new Date(checkIn + 'T00:00:00.000Z').getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const total = nights > 0 ? (parseFloat(pricePerNight) * nights).toFixed(2) : '0';
  const deposit = nights > 0 ? (parseFloat(total) * depositPercent / 100).toFixed(2) : '0';
  const remaining = nights > 0 ? (parseFloat(total) - parseFloat(deposit)).toFixed(2) : '0';

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Render a month grid.
  const renderMonth = (monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: Array<ReactNode> = [];

    // Empty cells before the first day.
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-10" />);
    }

    // Days of the month.
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = availability.get(dateStr);
      // PR fix: dates with NO availability data are AVAILABLE by default.
      // Only dates explicitly marked as available:false are blocked.
      const isAvailable = dayData ? dayData.available : true;
      const isCheckIn = checkIn === dateStr;
      const isCheckOut = checkOut === dateStr;
      const isInRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;
      const today = new Date().toISOString().slice(0, 10);
      const isPast = dateStr < today;

      let className = 'h-10 flex items-center justify-center text-sm rounded-md cursor-pointer transition ';
      if (isPast) {
        className += 'text-muted-foreground/40 cursor-not-allowed ';
      } else if (isCheckIn || isCheckOut) {
        className += 'bg-primary text-primary-foreground font-semibold ';
      } else if (isInRange) {
        className += 'bg-primary/10 text-primary ';
      } else if (isAvailable) {
        className += 'hover:bg-primary/10 text-foreground ';
      } else {
        // Only show line-through for dates explicitly blocked
        // (available:false in the availability data).
        className += 'text-muted-foreground/40 cursor-not-allowed line-through ';
      }

      days.push(
        <div
          key={dateStr}
          className={className}
          onClick={() => !isPast && handleDateClick(dateStr)}
          role="button"
          tabIndex={isAvailable && !isPast ? 0 : -1}
          aria-label={`${dateStr} ${isAvailable && !isPast ? 'disponible' : 'no disponible'}`}
        >
          {day}
        </div>,
      );
    }

    return (
      <div key={`${year}-${month}`} className="flex-1">
        <h3 className="text-sm font-semibold text-center mb-3">
          {MONTH_NAMES[month]} {year}
        </h3>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DAY_NAMES.map((day) => (
            <div key={day} className="text-xs text-muted-foreground text-center font-medium">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Calendar className="size-5" aria-hidden="true" />
          Selecciona las fechas
        </h3>
        {loading && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
      </div>

      {/* Calendar navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="icon" onClick={prevMonth} aria-label="Mes anterior">
          <ChevronLeft className="size-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={nextMonth} aria-label="Mes siguiente">
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Two-month view */}
      <div className="flex gap-6">
        {renderMonth(currentMonth)}
        {renderMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
      </div>

      {/* Booking summary */}
      {nights > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {pricePerNight} USDT × {nights} {nights === 1 ? 'noche' : 'noches'}
            </span>
            <span className="font-medium">{total} USDT</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Depósito ({depositPercent}%)</span>
            <span className="font-medium text-emerald-600 dark:text-emerald-400">{deposit} USDT</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Restante al check-in</span>
            <span className="font-medium">{remaining} USDT</span>
          </div>
          <div className="border-t pt-2 flex justify-between font-semibold">
            <span>Total</span>
            <span>{total} USDT</span>
          </div>
          {checkInTime && (
            <p className="text-xs text-muted-foreground">Entrada: {checkInTime}</p>
          )}
          {checkOutTime && (
            <p className="text-xs text-muted-foreground">Salida: {checkOutTime}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {cancellationPolicy === 'free_48h'
              ? 'Cancelación gratuita hasta 48h antes del check-in'
              : 'Sin reembolso'}
          </p>
          <Button
            className="w-full"
            onClick={handleReserve}
            disabled={booking || !checkIn || !checkOut}
          >
            {booking ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
            {booking ? 'Reservando...' : 'Reservar'}
          </Button>
        </div>
      )}

      {!nights && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <AlertCircle className="size-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>Selecciona tu fecha de entrada y salida en el calendario.</span>
        </div>
      )}
    </div>
  );
}
